import React from 'react';
import { getTranslatedEmoteSpeech } from '../../utils/emoteSpeechI18n';
import {
  MOBILE_EMOTE_PICKER_MAX_HEIGHT_PX,
  MOBILE_EMOTE_PICKER_MAX_WIDTH_PX,
  MOBILE_EMOTE_PICKER_MIN_WIDTH_PX,
  MOBILE_EMOTE_PICKER_TOP_PX,
} from '../mobile/mobileBattleLayoutOffsets';
import './BattleEmotePicker.css';

/**
 * Center-screen emote picker (desktop + mobile battle).
 */
export default function BattleEmotePicker({
  isMobile = false,
  emotesLoading,
  emotesError,
  playerEmotes,
  playerEmoteSelections,
  onRetryFetchEmotes,
  t,
  ws,
  gameId,
  setShowEmoteButtons,
  setCurrentEmoteBubble,
  onPickerInteraction,
}) {
  const renderBody = () => {
    if (emotesLoading) {
      return (
        <div className="battle-emote-picker__status">
          {t('profile.emotes.loading')}
        </div>
      );
    }

    if (emotesError) {
      return (
        <div className="battle-emote-picker__error">
          <div style={{ marginBottom: '8px' }}>{t('profile.emotes.loadFailed')}</div>
          <button
            type="button"
            className="battle-emote-picker__retry"
            onClick={() => {
              onRetryFetchEmotes();
            }}
          >
            {t('profile.emotes.retry')}
          </button>
        </div>
      );
    }

    if (playerEmotes.length === 0) {
      return (
        <div className="battle-emote-picker__status">
          {t('profile.emotes.noneAvailable')}
        </div>
      );
    }

    const selectedEmotes = playerEmotes.filter((emote) => {
      if (!emote.isUnlocked) {
        return false;
      }
      if (!emote.name) {
        return false;
      }
      const selectedUnlockKey = playerEmoteSelections[emote.name];
      if (!selectedUnlockKey) {
        return false;
      }
      if (emote.unlockKey !== selectedUnlockKey) {
        return false;
      }
      return true;
    });

    if (selectedEmotes.length === 0) {
      return (
        <div className="battle-emote-picker__status">
          {t('profile.emotes.noneSelected')}
        </div>
      );
    }

    const grouped = selectedEmotes.reduce((acc, emote) => {
      const type = emote.name;
      if (!acc[type]) acc[type] = [];
      acc[type].push(emote);
      return acc;
    }, {});

    return Object.entries(grouped).map(([emoteType, emotes]) => (
      <div key={emoteType} className="battle-emote-picker__group">
        {emotes.map((emote) => (
          <button
            key={emote.unlockKey}
            type="button"
            className="battle-emote-picker__button"
            onClick={() => {
              if (!emote.unlockKey || typeof emote.unlockKey !== 'string') {
                throw new Error('Emote row missing unlockKey');
              }
              const emoteText = getTranslatedEmoteSpeech(emote.unlockKey, t);

              setCurrentEmoteBubble({
                emoteKey: emote.unlockKey,
                timestamp: Date.now(),
              });

              setTimeout(() => {
                setCurrentEmoteBubble(null);
              }, 3000);

              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                  type: 'emote',
                  gameId,
                  emoteType,
                  emoteKey: emote.unlockKey,
                  text: emoteText,
                }));
              }

              setShowEmoteButtons(false);
            }}
          >
            {getTranslatedEmoteSpeech(emote.unlockKey, t)}
          </button>
        ))}
      </div>
    ));
  };

  return (
    <div
      data-emote-buttons
      className={[
        'battle-emote-picker',
        isMobile ? 'battle-emote-picker--mobile' : 'battle-emote-picker--desktop',
      ].join(' ')}
      style={
        isMobile
          ? {
              '--mobile-emote-picker-top': `${MOBILE_EMOTE_PICKER_TOP_PX}px`,
              '--mobile-emote-picker-min-width': `${MOBILE_EMOTE_PICKER_MIN_WIDTH_PX}px`,
              '--mobile-emote-picker-max-width': `${MOBILE_EMOTE_PICKER_MAX_WIDTH_PX}px`,
              '--mobile-emote-picker-max-height': `${MOBILE_EMOTE_PICKER_MAX_HEIGHT_PX}px`,
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onPickerInteraction}
    >
      {renderBody()}
    </div>
  );
}
